import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  Calendar,
  CheckCircle,
  DollarSign,
  Info,
  Target,
  TrendingDown,
  TrendingUp,
} from 'lucide-react';
import { useState } from 'react';
import { trpc } from '../../utils/trpc';
import { Alert, AlertDescription, AlertTitle } from '../ui/alert';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { CategoryTrendsChart } from './category-trends-chart';
import { SpendingPatternsCard } from './spending-patterns-card';
import { SpendingTrendsChart } from './spending-trends-chart';

export function TrendsDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState<
    'daily' | 'weekly' | 'monthly'
  >('monthly');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Get current household
  const { data: household } = useQuery(trpc.household.current.queryOptions());
  const householdId = household?.id;

  const { data: trendsSummary, isLoading: isLoadingSummary } = useQuery({
    ...trpc.trends.getTrendsSummary.queryOptions({}),
    enabled: !!householdId,
  });

  const { data: spendingTrends, isLoading: isLoadingSpending } = useQuery({
    ...trpc.trends.getSpendingTrends.queryOptions({
      periodType: selectedPeriod,
      limit: 12,
    }),
    enabled: !!householdId,
  });

  const { data: categoryTrends, isLoading: isLoadingCategories } = useQuery({
    ...trpc.trends.getCategoryTrends.queryOptions({
      monthsBack: 12,
    }),
    enabled: !!householdId,
  });

  const { data: userPatterns, isLoading: isLoadingPatterns } = useQuery({
    ...trpc.trends.getUserSpendingPatterns.queryOptions({}),
    enabled: !!householdId,
  });

  const { data: categoryInsights } = useQuery({
    ...trpc.trends.getCategoryInsights.queryOptions({
      categoryId: selectedCategory!,
    }),
    enabled: !!householdId && !!selectedCategory,
  });

  const analyzeSpendingTrends = useMutation(
    trpc.trends.analyzeSpendingTrends.mutationOptions()
  );
  const analyzeCategoryTrends = useMutation(
    trpc.trends.analyzeCategoryTrends.mutationOptions()
  );
  const detectSpendingPatterns = useMutation(
    trpc.trends.detectSpendingPatterns.mutationOptions()
  );

  const handleAnalyzeTrends = async () => {
    const endDate = new Date();
    const startDate = new Date();
    startDate.setMonth(startDate.getMonth() - 3);

    try {
      await Promise.all([
        analyzeSpendingTrends.mutateAsync({
          periodType: selectedPeriod,
          startDate,
          endDate,
        }),
        analyzeCategoryTrends.mutateAsync({
          monthsBack: 12,
        }),
        detectSpendingPatterns.mutateAsync({}),
      ]);

      // Refetch data after analysis
      window.location.reload();
    } catch (error) {
      console.error('Error analyzing trends:', error);
    }
  };

  const getInsightIcon = (type: string) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-orange-500" />;
      case 'positive':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'info':
        return <Info className="h-4 w-4 text-blue-500" />;
      default:
        return <Info className="h-4 w-4" />;
    }
  };

  const getInsightColor = (type: string, severity: string) => {
    if (type === 'warning' && severity === 'high') return 'destructive';
    if (type === 'warning') return 'default';
    if (type === 'positive') return 'default';
    return 'secondary';
  };

  if (!householdId || isLoadingSummary) {
    return (
      <div className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <Card key={`loading-card-${i}`}>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-2">
                  <div className="h-4 w-3/4 rounded bg-muted" />
                  <div className="h-8 rounded bg-muted" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header with Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-bold text-3xl tracking-tight bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
            Análise de Tendências
          </h2>
          <p className="text-muted-foreground">
            Identifique padrões e tendências nos seus gastos com insights inteligentes
          </p>
        </div>
        <Button
          className="bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
          disabled={analyzeSpendingTrends.isPending}
          onClick={handleAnalyzeTrends}
        >
          {analyzeSpendingTrends.isPending
            ? 'Analisando...'
            : 'Atualizar Análise'}
        </Button>
      </div>

      {/* Summary Cards */}
      {trendsSummary && (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-sm">
                Gasto Médio Mensal
              </CardTitle>
              <DollarSign className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">
                R${' '}
                {trendsSummary.summary.avgMonthlySpending.toLocaleString(
                  'pt-BR',
                  {
                    minimumFractionDigits: 2,
                  }
                )}
              </div>
              <p className="text-muted-foreground text-xs">
                Últimos {trendsSummary.summary.periodsAnalyzed} meses
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-sm">
                Categorias Analisadas
              </CardTitle>
              <Target className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">
                {trendsSummary.summary.categoriesAnalyzed}
              </div>
              <p className="text-muted-foreground text-xs">
                Com dados históricos
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-sm">
                Padrões Detectados
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">
                {trendsSummary.summary.patternsDetected}
              </div>
              <p className="text-muted-foreground text-xs">
                Comportamentos identificados
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="font-medium text-sm">
                Períodos Analisados
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="font-bold text-2xl">
                {trendsSummary.summary.periodsAnalyzed}
              </div>
              <p className="text-muted-foreground text-xs">Meses com dados</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Main Content Tabs */}
      <Tabs className="space-y-6" defaultValue="overview">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="spending">Gastos por Período</TabsTrigger>
          <TabsTrigger value="categories">Tendências por Categoria</TabsTrigger>
          <TabsTrigger value="patterns">Padrões de Comportamento</TabsTrigger>
        </TabsList>

        <TabsContent className="space-y-6" value="overview">
          {/* Top Categories */}
          {trendsSummary?.topCategories && (
            <Card>
              <CardHeader>
                <CardTitle>Top 5 Categorias por Gasto</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {trendsSummary.topCategories.map((category: any) => (
                    <div
                      className="flex items-center justify-between"
                      key={category.categoryId}
                    >
                      <div className="flex items-center space-x-2">
                        <span className="font-medium">
                          {category.categoryName}
                        </span>
                        <Badge variant="secondary">
                          {category.transactionCount} transações
                        </Badge>
                      </div>
                      <div className="text-right">
                        <div className="font-bold">
                          R${' '}
                          {category.totalAmount.toLocaleString('pt-BR', {
                            minimumFractionDigits: 2,
                          })}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Recent Monthly Trends */}
          {trendsSummary?.monthlyTrends && (
            <Card>
              <CardHeader>
                <CardTitle>Tendência de Gastos Mensais</CardTitle>
              </CardHeader>
              <CardContent>
                <SpendingTrendsChart data={trendsSummary.monthlyTrends} />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent className="space-y-6" value="spending">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-lg">Gastos por Período</h3>
            <Select
              onValueChange={(value: 'daily' | 'weekly' | 'monthly') =>
                setSelectedPeriod(value)
              }
              value={selectedPeriod}
            >
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Selecionar período" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="daily">Diário</SelectItem>
                <SelectItem value="weekly">Semanal</SelectItem>
                <SelectItem value="monthly">Mensal</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {spendingTrends && !isLoadingSpending ? (
            <Card>
              <CardContent className="p-6">
                <SpendingTrendsChart data={spendingTrends} />
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="h-64 animate-pulse rounded bg-muted" />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent className="space-y-6" value="categories">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-lg">Tendências por Categoria</h3>
          </div>

          {categoryTrends && !isLoadingCategories ? (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Evolução das Categorias</CardTitle>
                </CardHeader>
                <CardContent>
                  <CategoryTrendsChart data={categoryTrends} />
                </CardContent>
              </Card>

              {/* Category Selection and Insights */}
              <div className="grid gap-6 lg:grid-cols-2">
                <Card>
                  <CardHeader>
                    <CardTitle>Análise Detalhada por Categoria</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      <Select
                        onValueChange={setSelectedCategory}
                        value={selectedCategory || ''}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecionar categoria para análise" />
                        </SelectTrigger>
                        <SelectContent>
                          {Array.from(
                            new Set(categoryTrends.map((t) => t.categoryId))
                          ).map((categoryId) => {
                            const category = categoryTrends.find(
                              (t) => t.categoryId === categoryId
                            );
                            return (
                              <SelectItem key={categoryId} value={categoryId}>
                                {category?.categoryName}
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>

                      {categoryInsights && (
                        <div className="space-y-4">
                          <div className="grid gap-2">
                            <div className="text-muted-foreground text-sm">
                              Total Gasto
                            </div>
                            <div className="font-bold text-2xl">
                              R${' '}
                              {categoryInsights.summary?.totalSpent.toLocaleString(
                                'pt-BR',
                                {
                                  minimumFractionDigits: 2,
                                }
                              )}
                            </div>
                          </div>

                          {categoryInsights.summary?.latestGrowthRate && (
                            <div className="flex items-center space-x-2">
                              {categoryInsights.summary.latestGrowthRate > 0 ? (
                                <TrendingUp className="h-4 w-4 text-red-500" />
                              ) : (
                                <TrendingDown className="h-4 w-4 text-green-500" />
                              )}
                              <span
                                className={`text-sm ${
                                  categoryInsights.summary.latestGrowthRate > 0
                                    ? 'text-red-500'
                                    : 'text-green-500'
                                }`}
                              >
                                {Math.abs(
                                  categoryInsights.summary.latestGrowthRate
                                ).toFixed(1)}
                                % vs mês anterior
                              </span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Insights */}
                {categoryInsights?.insights && (
                  <Card>
                    <CardHeader>
                      <CardTitle>Insights da Categoria</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-3">
                        {categoryInsights.insights.map(
                          (insight: any, index: number) => (
                            <Alert
                              key={`insight-${index}`}
                              variant={getInsightColor(
                                insight.type,
                                insight.severity
                              )}
                            >
                              <div className="flex items-start space-x-2">
                                {getInsightIcon(insight.type)}
                                <div>
                                  <AlertTitle className="text-sm">
                                    {insight.title}
                                  </AlertTitle>
                                  <AlertDescription className="text-xs">
                                    {insight.message}
                                  </AlertDescription>
                                </div>
                              </div>
                            </Alert>
                          )
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          ) : (
            <Card>
              <CardContent className="p-6">
                <div className="animate-pulse space-y-4">
                  <div className="h-64 rounded bg-muted" />
                  <div className="grid gap-4 lg:grid-cols-2">
                    <div className="h-32 rounded bg-muted" />
                    <div className="h-32 rounded bg-muted" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent className="space-y-6" value="patterns">
          <div className="flex items-center justify-between">
            <h3 className="font-medium text-lg">Padrões de Comportamento</h3>
          </div>

          {userPatterns && !isLoadingPatterns ? (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
              {userPatterns.map((pattern) => (
                <SpendingPatternsCard key={pattern.id} pattern={{
                  ...pattern,
                  patternData: pattern.patternData || {},
                  lastOccurrence: pattern.lastOccurrence ? new Date(pattern.lastOccurrence) : undefined
                }} />
              ))}

              {userPatterns.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="p-6 text-center">
                    <p className="text-muted-foreground">
                      Nenhum padrão de comportamento detectado ainda. Continue
                      usando o sistema para que possamos identificar seus
                      hábitos de gasto.
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : (
            <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Card key={`pattern-loading-${i}`}>
                  <CardContent className="p-6">
                    <div className="animate-pulse space-y-4">
                      <div className="h-4 w-3/4 rounded bg-muted" />
                      <div className="h-16 rounded bg-muted" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
